import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const MAP_STYLE_URL = process.env.REACT_APP_MAP_STYLE_URL || 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const DEFAULT_CENTER = [-52.95, -22.55];

function isValidCoord(value) {
  return Number.isFinite(value);
}

export default function AddressMapPicker({ value, onChange, height = 260 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const initial = value && isValidCoord(value.lat) && isValidCoord(value.lng)
      ? [value.lng, value.lat]
      : DEFAULT_CENTER;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: initial,
      zoom: 12,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');

    const handleClick = (event) => {
      const { lat, lng } = event.lngLat;
      if (onChangeRef.current) {
        onChangeRef.current({ lat, lng });
      }
    };

    map.on('click', handleClick);

    mapRef.current = map;

    return () => {
      const instance = mapRef.current;
      const container = containerRef.current;
      mapRef.current = null;
      if (instance) {
        try {
          instance.off('click', handleClick);
        } catch {
          // ignore
        }
        const safeRemove = () => {
          try {
            if (!container || !container.isConnected) {
              return;
            }
            const canvas = instance.getCanvas ? instance.getCanvas() : null;
            if (!canvas || (container.contains(canvas))) {
              instance.remove();
            }
          } catch {
            // ignore remove errors on unmount
          }
        };
        setTimeout(safeRemove, 0);
      }
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (value && isValidCoord(value.lat) && isValidCoord(value.lng)) {
      const lngLat = [value.lng, value.lat];
      if (!markerRef.current) {
        markerRef.current = new maplibregl.Marker({ color: '#ff6b00' }).setLngLat(lngLat).addTo(map);
      } else {
        markerRef.current.setLngLat(lngLat);
      }
      map.easeTo({ center: lngLat, zoom: Math.max(map.getZoom(), 14), duration: 500 });
    }
  }, [value?.lat, value?.lng]);

  return (
    <div style={{ width: '100%', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      <div ref={containerRef} style={{ width: '100%', height }} />
    </div>
  );
}
